import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import { theme } from '@/core/theme';
import { computeText } from '@/locales/utils';
import {
    ModalInputTitle,
    ModalTextInput,
} from '@/pages/MenuList/components/MenuModal/CreateMenuModal/CreateMenuModal.style';
import { ModalInputContainer } from '@/pages/MenuList/components/MenuModal/CreateMenuModal/CreateMenuModal.style';
import { getUserToken } from '@/redux/user';
import { useGetLocationAutocompleteQuery } from '@/services/innovorder/pickupPoint/pickupPoint.endpoints';

import { useSelector } from 'react-redux';
import { MultiSelect } from '@/components/form/MultiSelect';
import { SelectOptionInterface } from '@/components/atoms/MultiSelect/MultiSelect.interface';
import { InputActionMeta } from 'react-select';
import { EditPickupPointStepProps } from './EditPickupPoint.type';
import { EditPickupPointFormValues } from '../CreatePickupPointModal/CreatePickupPointModal.type';

export const EditPickupPointStep: React.FunctionComponent<React.PropsWithChildren<EditPickupPointStepProps>> = ({
    isLoading,
}) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const { control } = useFormContext<EditPickupPointFormValues>();
    const [sessionToken] = useState(() => crypto.randomUUID());

    const [searchText, setSearchText] = useState('');

    const { data: locationData } = useGetLocationAutocompleteQuery(
        {
            searchText,
            sessiontoken: sessionToken,
            token: token || '',
        },
        { skip: searchText.length <= 2 },
    );

    const options = React.useMemo(
        () =>
            locationData?.suggestions.map((suggestion) => ({
                label: suggestion.name,
                value: suggestion.name,
            })) ?? [],
        [locationData],
    );

    const handleInputChange = (value: string, actionMeta: InputActionMeta) => {
        if (actionMeta.action === 'input-change') {
            setSearchText(value);
        }
    };

    return (
        <>
            <Controller
                control={control}
                name="name"
                rules={{
                    required: computeText(intl, 'field.required'),
                    validate: (value = '') => !!value?.length,
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <ModalInputContainer>
                        <ModalInputTitle type="small" weight="medium" text="pickupPoint.modal.pickupPoint.name" />
                        <ModalTextInput
                            type="string"
                            data-testid="pickupPoint-modal-pickupPoint-name-input"
                            error={error?.message}
                            onChange={onChange}
                            value={value?.toString()}
                            disabled={isLoading}
                        />
                    </ModalInputContainer>
                )}
            />

            <Controller
                control={control}
                name="address"
                render={() => (
                    <>
                        <ModalInputTitle type="small" weight="medium" text="pickupPoint.modal.pickupPoint.address" />
                        <MultiSelect
                            name="address"
                            transform={{
                                input: (value: string) => (value ? [{ label: value, value }] : undefined),
                                output: (option: unknown) => (option as SelectOptionInterface<string>)?.value,
                            }}
                            options={options}
                            placeholder={computeText(intl, 'pickupPoint.modal.pickupPoint.address.placeholder')}
                            onInputChange={handleInputChange}
                            isLoading={isLoading}
                        />
                    </>
                )}
            />

            <Controller
                control={control}
                name="additionalAddressInfo"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <ModalInputContainer>
                        <ModalInputTitle
                            type="small"
                            weight="medium"
                            text="pickupPoint.modal.pickupPoint.additionalAddressInfo"
                        />
                        <ModalTextInput
                            type="string"
                            data-testid="pickupPoint-modal-pickupPoint-additionalAddressInfo-input"
                            error={error?.message}
                            onChange={onChange}
                            value={value?.toString()}
                            disabled={isLoading}
                        />
                    </ModalInputContainer>
                )}
            />
        </>
    );
};
